import React, { useState, useCallback, useEffect } from "react";
import usePageTitle from "../../hooks/usePageTitle";

// Components
import { DashboardShimmerLoader } from "../../components/loaders/DashboardShimmerLoader";
import { OverviewTab } from "../../components/dashboard/OverviewTab";
import ConnectDatabaseDialog from "../../components/dialogs/ConnectDatabaseDialog";

// Services & Utils
import { APIs } from "../../services/Apis";
import { toast } from "react-toastify";
import { convertStakeholdersToFlowDiagram } from "../../services/FlowConstants";

export default function DashboardPage() {
  usePageTitle("Dashboard");

  // Data state
  const [segments, setSegments] = useState([]);
  const [segmentEdges, setSegmentEdges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dbActive, setDbActive] = useState(true);
  const [globalActiveWorkFlow, setGlobalActiveWorkFlow] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [isSampleDb, setIsSampleDb] = useState(false);

  const [connectDbDialogOpen, setConnectDbDialogOpen] = useState(false);

  // Data fetching
  const fetchDashboardDetails = useCallback(async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      setError(null);
      const responseData = await APIs.getDashboardDetails();

      if (responseData.success) {
        const { nodes, edges } = convertStakeholdersToFlowDiagram(
          responseData.segments,
        );

        let sortedSegments = responseData.segments.map((segment) => ({
          ...segment,
          triggers: [...segment.triggers]
            .sort((a, b) => Number(a.sortIndex) - Number(b.sortIndex))
            .map((trigger) => ({
              ...trigger,
              actions: [...trigger.actions].sort(
                (a, b) => Number(a.sortIndex) - Number(b.sortIndex),
              ),
            })),
        }));

        setSegments(sortedSegments || []);
        setSegmentEdges(edges || []);
        setDbActive(responseData.dbActive !== false);
        setGlobalActiveWorkFlow(responseData.active === true);
        setDisplayName(responseData.displayName || "");
        setBusinessName(responseData.businessName || "");
        setIsSampleDb(responseData.dbConnected?.isSample === true);
      } else {
        setError("Failed to load dashboard details");
        toast.error("Failed to load dashboard details");
      }
    } catch (err) {
      console.error("Error fetching dashboard details:", err);
      setError("An error occurred while loading dashboard details");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardDetails();
  }, [fetchDashboardDetails]);

  // Poll every 30 seconds until all components are ready
  useEffect(() => {
    const checkAllReady = (segments) => {
      if (!segments || segments.length === 0) return true;
      for (const segment of segments) {
        if (segment.triggers) {
          for (const trigger of segment.triggers) {
            if (trigger.ready === false) return false;
            if (trigger.actions) {
              for (const action of trigger.actions) {
                if (action.ready === false) return false;
              }
            }
          }
        }
      }
      return true;
    };

    if (segments.length > 0 && !checkAllReady(segments)) {
      const intervalId = setInterval(() => {
        fetchDashboardDetails(false);
      }, 30000);

      return () => {
        clearInterval(intervalId);
      };
    }
  }, [segments, fetchDashboardDetails]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await fetchDashboardDetails(false);
    } finally {
      setRefreshing(false);
    }
  }, [fetchDashboardDetails]);

  if (loading) {
    return <DashboardShimmerLoader />;
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <p className="text-sm text-red-600">{error}</p>
            <p className="mt-2 text-xs text-gray-500">
              Please refresh the page or try again later.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <OverviewTab
        segments={segments}
        segmentEdges={segmentEdges}
        onRefresh={handleRefresh}
        loading={loading}
        isOnboarding={false}
        dbActive={dbActive}
        globalActive={globalActiveWorkFlow}
        displayName={displayName}
        businessName={businessName}
        refreshing={refreshing}
        onConnectDb={() => setConnectDbDialogOpen(true)}
        isSampleDb={isSampleDb}
      />

      <ConnectDatabaseDialog
        open={connectDbDialogOpen}
        setOpen={setConnectDbDialogOpen}
      />
    </>
  );
}
